import * as React from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL;

interface City {
  citiesId: number;
  name: string;
}
interface Shop {
  shopId: number;
  name: string;
  address: string;
  city: City;
  manager?: { managerId: number; name: string } | null;
}
interface Manager {
  managerId: number;
  name: string;
  managerChatId: string | null;
  shops: Shop[];
}

export default function ManagerEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [manager, setManager] = React.useState<Manager | null>(null);
  const [name, setName] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  // Поиск магазинов
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<Shop[]>([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [selectedShop, setSelectedShop] = React.useState<Shop | null>(null);
  const [assigning, setAssigning] = React.useState(false);

  const fetchManager = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/managers/${id}`);
      setManager(data);
      setName(data.name);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchManager();
  }, [id]);

  React.useEffect(() => {
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/shops-search`, {
          params: { q: searchQuery, managerId: id },
        });
        setSearchResults(data);
      } catch (err) {
        console.error(err);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSaveName = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/managers/${id}`, { name });
      setManager((prev) => (prev ? { ...prev, name } : prev));
      alert('Збережено');
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveShop = async (shopId: number) => {
    if (!confirm('Відкріпити цей магазин від менеджера?')) return;
    try {
      await axios.put(`${API_URL}/remove-manager-from-shop/${shopId}`);
      fetchManager();
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleAssignShop = async () => {
    if (!selectedShop) return;
    setAssigning(true);
    try {
      await axios.put(`${API_URL}/shops/${selectedShop.shopId}`, {
        managerId: Number(id),
      });
      setSelectedShop(null);
      setSearchQuery('');
      fetchManager();
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setAssigning(false);
    }
  };

  if (loading)
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  if (!manager)
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Менеджера не знайдено</Typography>
      </Box>
    );

  return (
    <Box sx={{ p: 2, mx: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
        variant="text"
      >
        Назад
      </Button>

      <Typography variant="h5" sx={{ mb: 3 }}>
        Редагування менеджера
      </Typography>

      {/* Блок імені */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 1.5, color: 'text.secondary' }}
        >
          Ім'я менеджера
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ім'я менеджера"
          />
          <Button
            variant="contained"
            onClick={handleSaveName}
            disabled={saving || !name.trim() || name === manager.name}
          >
            {saving ? 'Збереження...' : 'Зберегти'}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 1.5, color: 'text.secondary' }}
        >
          Додати магазин
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Autocomplete
            fullWidth
            size="small"
            options={searchResults}
            getOptionLabel={(o) => `${o.name} — ${o.city?.name ?? ''}`}
            filterOptions={(x) => x}
            loading={searchLoading}
            value={selectedShop}
            onChange={(_, val) => setSelectedShop(val)}
            inputValue={searchQuery}
            onInputChange={(_, val) => setSearchQuery(val)}
            onOpen={async () => {
              if (searchResults.length > 0) return;
              setSearchLoading(true);
              try {
                const { data } = await axios.get(`${API_URL}/shops-search`, {
                  params: { q: '', managerId: id },
                });
                setSearchResults(data);
              } catch (e) {
                console.error(e);
              } finally {
                setSearchLoading(false);
              }
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.shopId}>
                <Box>
                  <Typography fontSize={14}>{option.name}</Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {option.city?.name} · {option.address}
                    {option.manager && (
                      <span style={{ color: '#f44336' }}>
                        {' '}
                        · зайнятий: {option.manager.name}
                      </span>
                    )}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Пошук магазину за назвою..."
              />
            )}
          />
          <Button
            variant="contained"
            onClick={handleAssignShop}
            disabled={!selectedShop || assigning}
          >
            {assigning ? 'Додавання...' : 'Додати'}
          </Button>
        </Box>
        {selectedShop?.manager && (
          <Typography fontSize={12} color="error" sx={{ mt: 1 }}>
            Увага: цей магазин вже прикріплений до менеджера «
            {selectedShop.manager.name}». Після збереження він буде
            переприкріплений.
          </Typography>
        )}
      </Paper>

      {/* Блок магазинів */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Магазини ({manager.shops.length})
          </Typography>
        </Box>

        {manager.shops.length === 0 ? (
          <Typography
            sx={{ px: 2, py: 2, fontSize: 13, color: 'text.secondary' }}
          >
            Немає прикріплених магазинів
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Назва</TableCell>
                  <TableCell>Адреса</TableCell>
                  <TableCell>Місто</TableCell>
                  <TableCell align="right">Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {manager.shops.map((shop) => (
                  <TableRow key={shop.shopId} hover>
                    <TableCell>{shop.name}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {shop.address}
                    </TableCell>
                    <TableCell>
                      <Chip label={shop.city?.name ?? '—'} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/shop/${shop.shopId}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveShop(shop.shopId)}
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
